import React, { useState } from 'react';
import { BlockReason, Step } from 'features/card/block-card/model/types';
import Reason from 'features/card/block-card/ui/Reason';
import { Card, CStatus } from 'entities/card/model/types';
import Success from 'features/card/block-card/ui/Success';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'shared/config/store/store';
import { setActivity } from 'shared/config/store/reducers/appSlice';
import { useBlockMutation } from 'features/card/block-card/api';
import { Account } from 'entities/account/model/types';
import { getEMICurrentAccountObject } from 'shared/config/store/reducers/accountsSlice';
import { getAccountCards, updateCard } from 'shared/config/store/reducers/cardsSlice';
import { EMI } from 'shared/model/types';
import { ApiError } from 'shared/model/types';
import { getCurrentEmi } from 'shared/config/store/selectors/app';
import { BackButton, GridContainer } from 'shared/ui';
import { Flex } from 'antd';
import ConfirmModal from '../components/ConfirmModal';

const Block: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const currentEmi: EMI = useSelector(getCurrentEmi);
  const currentAccount: Account = useSelector((state: RootState) =>
    getEMICurrentAccountObject(state, currentEmi)
  ) as Account;
  const card: Card = useSelector(getAccountCards(currentEmi, currentAccount.id))?.[0];

  const [block] = useBlockMutation();

  const [step, setStep] = useState<Step>(Step.Reason);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingReason, setPendingReason] = useState<BlockReason | null>(null);

  const handleReasonSubmit = (selectedReason: BlockReason) => {
    setPendingReason(selectedReason);
    setIsModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setPendingReason(null);
  };

  const handleModalConfirm = () => {
    if (pendingReason) {
      onBlock(pendingReason);
    }
    setIsModalOpen(false);
    setPendingReason(null);
  };

  const onBlock = async (selectedReason: BlockReason) => {
    dispatch(setActivity(true));
    await block({
      accountId: currentAccount.id,
      cardId: card.id,
      reason: selectedReason
    })
      .unwrap()
      .then(() => {
        setStep(Step.Success);
        dispatch(
          updateCard({
            accountId: currentAccount.id,
            emi: currentEmi,
            cardId: card.id,
            newCard: { status: CStatus.Blocked }
          })
        );
      })
      .catch((err: ApiError) => {
        console.error('Failed to block card:', err);
      })
      .finally(() => {
        dispatch(setActivity(false));
      });
  };

  return (
    <GridContainer>
      <Flex vertical style={{ flex: 1 }}>
        <Flex align='center' gap={10} style={{ marginBottom: 24 }}>
          <BackButton />
        </Flex>
        <div style={{ flex: 1 }}>
          {step === Step.Reason && (
            <Reason
              cardNumber={card.numberMasked}
              cardType={card.type}
              onSubmit={handleReasonSubmit}
            />
          )}
          {step === Step.Success && <Success cardNumber={card.numberMasked} />}

          <ConfirmModal open={isModalOpen} onCancel={handleModalCancel} onOk={handleModalConfirm} />
        </div>
      </Flex>
    </GridContainer>
  );
};

export default Block;
